(ns portal.login-page.view
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            [clojure.string :as str]
            [common.localstorage :as local-storage]
            [common.os :as os]
            [common.resource.auth :as auth]
            [common.resource.sso :as sso]
            [common.url :as url]
            [common.validators.core :as validators]
            [common.widgets.auth-card :refer [auth-card]]
            [common.widgets.form-field :refer [form-field]]
            [common.widgets.input :refer [input]]
            [common.widgets.page :as page]
            [portal.login-page.hooks :as h]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [defui $]]))

(def form-validators
  {:email [{:validator (complement str/blank?)
            :error-message "The field should not be empty"}
           {:validator validators/valid-email?
            :error-message "Not a valid email address"}]
   :password [{:validator (complement str/blank?)
               :error-message "The field should not be empty"}]})

;;
;; re-frame
;;

(rf/reg-fx ::-apply
  (fn [f] (f)))

(rf/reg-event-fx ::apply
  (fn [_ [_ f]]
    {:fx [[::-apply f]]}))

(defn continue? [state]
  (contains? #{:continue :login-failure} state))

;;
;; view
;;

;; nil -> :init -> :continue -> :login ?-> :login-failure
;; nil -> :init -> :sso -> :sso-return ?-> :sso-failure
;; nil -> :os-login
(defui login-page []
  (let [[{:keys [email password dirty state] :as form-values} set-form-state]
        (uix/use-state {:email ""
                        :password ""
                        :dirty #{}
                        :state nil})

        sso? (h/use-sso-enabled? email)
        sso-redirect-url (h/use-sso-login-url {:email email})

        hide-password-field? (or sso? (not (continue? state)))

        errors (h/use-form-errors form-values (cond-> form-validators
                                                hide-password-field? (dissoc :password)))

        is-field-dirty? (fn [field]
                          (contains? dirty field))]
    (uix/use-effect (fn []
                      ;; storing the from-parameter
                      (when-let [from (:from (url/query-parameters))]
                        (local-storage/save! auth/login-redirect-url-localstorage-key from))

                      ;; we are embedded in OS
                      (if (os/os-environment?)
                        (set-form-state #(assoc % :state :os-login))
                        (set-form-state #(assoc % :state :init)))

                      ;; return from sso (not applicable in OS mode)
                      (when-not (os/os-environment?)
                        (let [query (url/query-parameters)]
                          (when (:state query)
                            (set-form-state #(assoc % :query query, :state :sso-return))
                            (rf/dispatch [::sso/fetch-sso-token-with-code
                                          {:data query
                                           :on-failure [::apply (fn [] (set-form-state #(assoc % :state :sso-failure)))]}]))))

                      ;; sso-token in localstorage
                      #_(when-let [token (sso/get-token-from-localstorage)]
                          (set-form-state #(assoc % :state :sso-autologin))
                          (rf/dispatch [::sso/sso-login
                                        {:token token
                                         :on-failure [::apply (fn [] (set-form-state #(assoc % :state :sso-failure)))]}])))
                    [])
    ($ page/page
       {:sidebar false
        :appbar false}
       ($ page/content-padding
          (case state

            ;; start empty
            nil nil

            (:init :continue :login-failure)
            ($ auth-card {:sub-title "Sign in to continue"
                          :novalidate true
                          :on-submit (fn [_e]
                                       (cond
                                         ;; we can SSO
                                         sso?
                                         (do (set-form-state #(assoc % :state :sso-login))
                                             (set! (.-location js/window) sso-redirect-url))

                                         ;; ask password
                                         (continue? state)
                                         (do (set-form-state #(assoc % :state :login))
                                             (rf/dispatch [:resource.auth/login
                                                           {:data {:email email, :password password}
                                                            :on-failure [::apply (fn [] (set-form-state #(assoc % :state :login-failure)))]}]))

                                         ;; default
                                         :else (set-form-state #(assoc % :state :continue))))}
               ($ form-field
                  {:label "Email address"
                   :data-testid "login-email"
                   :err-msg (when (is-field-dirty? :email) (:email errors))}
                  ($ input
                     {:id "email"
                      :type "email"
                      :data-lpignore "false"
                      :autoComplete "on"
                      :data-testid "email"
                      :value email
                      :onChange #(set-form-state (fn [form-state]
                                                   (assoc form-state :email (.. % -target -value))))
                      :onBlur #(set-form-state (fn [form-state]
                                                 (update form-state :dirty conj :email)))}))
               (when-not hide-password-field?
                 ($ form-field
                    {:label "Password"
                     :data-testid "login-password"
                     :err-msg (when (is-field-dirty? :password) (:password errors))}
                    ($ input
                       {:id "password"
                        :type "password"
                        :data-lpignore "false"
                        :autoComplete "on"
                        :data-testid "password"
                        :value password
                        :onChange #(set-form-state (fn [form-state]
                                                     (assoc form-state :password (.. % -target -value))))
                        :onBlur #(set-form-state (fn [form-state]
                                                   (update form-state :dirty conj :password)))})))

               (cond
                 (= :login-failure state)
                 ($ Alert {:severity "error"
                           :data-testid "login-error-message"}
                    "Invalid email/password combination. "
                    ($ Link {:href (r.easy/href :common/forgot-password)
                             :color "text.primary"
                             :underline "hover"
                             :sx #js {:fontWeight 600}
                             :data-testid "forgot-password"}
                       "Did you forget your Password?"))

                 (= :sso-failure state)
                 ($ Alert {:severity "error"
                           :data-testid "login-error-message"}
                    "Authentication via Okta failed"))
               ($ Button
                  {:variant "contained"
                   :type "submit"
                   :disabled (boolean (seq errors))
                   :data-testid "submit"}
                  (if hide-password-field? "Continue" "Login")))

            :sso-login
            ($ auth-card {:sub-title "Redirecting to Okta..."}
               ($ Stack {:alignItems "center"}
                  ($ CircularProgress)))

            :sso-return
            ($ auth-card {:sub-title "Returning from Okta..."}
               ($ Stack {:alignItems "center"}
                  ($ CircularProgress)))

            :sso-failure
            ($ auth-card {:sub-title "Authentication via Okta failed"})

            :os-login
            ($ auth-card {:sub-title "Signing in via OS..."}
               ($ Stack {:alignItems "center"}
                  ($ CircularProgress)))

            :login
            ($ auth-card {:sub-title "Signing in..."}
               ($ Stack {:alignItems "center"}
                  ($ CircularProgress))))))))
